import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { api } from '../api';
import { formatDate } from '../kit/time';

// Ticket 94 (census 56 D12, ADR-0003): push deals out of a personal workspace
// into a company the user holds a seat in. Rendered only while the active
// workspace is personal and at least one company seat exists — owners push,
// company admins never pull. Each row moves the deal, its contact and every
// child row; "Move everything" runs the same primitive over the whole list.

const MAX_ROUNDS = 25; // 25 × server batch (200) per click; the button stays available after

const KIND_LABELS = {
  loan: 'Loan file',
  transaction: 'Transaction',
  mortgage_opportunity: 'Mortgage opportunity',
  re_opportunity: 'Real estate opportunity',
};

function errDescription(e) {
  const d = e?.response?.data?.detail;
  return (typeof d === 'object' ? d?.message : d) || String(e);
}

function summarize(counts) {
  const n = Object.values(counts || {}).reduce((a, b) => a + b, 0);
  return `${n} record${n === 1 ? '' : 's'} moved`;
}

export default function MoveDealsTab({ companies }) {
  const [target, setTarget] = useState(companies[0]?.tenant_id || '');
  const [items, setItems] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [busy, setBusy] = useState(null); // deal_id | 'all'

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/tenants/movable-deals');
      setItems(data.items || []);
      setTruncated(!!data.truncated);
    } catch { setItems([]); setTruncated(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const targetName = companies.find((c) => c.tenant_id === target)?.name || 'the company';

  const moveOne = async (item) => {
    if (busy) return;
    setBusy(item.deal_id);
    try {
      const { data } = await api.post('/tenants/move-deal', { kind: item.kind, deal_id: item.deal_id, to_tenant_id: target });
      toast({
        title: data.moved ? `Moved to ${targetName}` : 'Already moved',
        description: data.moved ? `${item.label}: ${summarize(data.counts)}.` : `${item.label} is already in ${targetName}.`,
      });
      await load();
    } catch (e) {
      toast({ title: 'Could not move deal', description: errDescription(e) });
      await load(); // the row may have moved elsewhere meanwhile
    } finally { setBusy(null); }
  };

  const moveAll = async () => {
    if (busy || !items?.length) return;
    const ok = await confirmDialog({
      title: `Move all ${items.length} deal${items.length === 1 ? '' : 's'} to ${targetName}?`,
      description: 'Each deal moves with its contact, documents, messages and links. They will no longer show in this personal workspace.',
      confirmText: 'Move everything',
    });
    if (!ok) return;
    setBusy('all');
    try {
      // The server moves one batch per call and reports `remaining`; keep
      // going while each round makes progress (a round that moves nothing
      // means every leftover row failed — stop and show them).
      let moved = 0;
      let failed = 0;
      for (let round = 0; round < MAX_ROUNDS; round += 1) {
        const { data } = await api.post('/tenants/move-all', { to_tenant_id: target });
        moved += data.moved || 0;
        failed = data.failed?.length || 0;
        if (!data.remaining || !data.moved) break;
      }
      toast({
        title: `Moved ${moved} deal${moved === 1 ? '' : 's'} to ${targetName}`,
        description: failed ? `${failed} could not be moved and ${failed === 1 ? 'is' : 'are'} still listed below.` : undefined,
      });
      await load();
    } catch (e) {
      toast({ title: 'Could not move deals', description: errDescription(e) });
      await load(); // some deals may have moved before the failure
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="move-deals-tab">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col md:flex-row md:items-end gap-4">
        <div className="flex-1">
          <div className="text-sm font-medium" style={{ color: '#070B5F' }}>Move deals into a company workspace</div>
          <p className="text-xs text-slate-500 mt-1">
            Deals you worked here before joining a company stay in this personal workspace until you move them.
            A move carries the deal, its contact and everything attached; only the workspace changes.
          </p>
        </div>
        <div className="w-full md:w-64">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Move to</div>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="move-deals-target"><SelectValue placeholder="Choose a workspace" /></SelectTrigger>
            <SelectContent>
              {companies.map((c) => <SelectItem key={c.tenant_id} value={c.tenant_id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <button
          type="button"
          onClick={moveAll}
          disabled={!!busy || !items?.length || !target}
          data-testid="move-deals-all"
          className="btn-gold disabled:opacity-50"
        >
          {busy === 'all' ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Move everything
        </button>
      </div>

      {items === null ? (
        <div className="text-slate-400 text-sm p-4"><Loader2 className="animate-spin inline mr-2" size={14} />Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-8 text-center text-slate-400 text-sm" data-testid="move-deals-empty">
          Nothing to move — every deal you own is already in a company workspace.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          {truncated && (
            <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-b border-slate-100" data-testid="move-deals-truncated">
              Showing the newest {items.length} deals. Move some and the list refreshes, or use Move everything.
            </div>
          )}
          {items.map((i) => (
            <div key={i.deal_id} className="flex items-center justify-between px-4 py-3 border-b border-slate-50" data-testid={`move-deal-row-${i.deal_id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: '#070B5F' }}>{i.label}</div>
                <div className="text-xs text-slate-500">
                  {KIND_LABELS[i.kind] || i.kind}
                  {i.is_deleted ? ' · deleted' : ''}
                  {i.created_at ? ` · ${formatDate(i.created_at)}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => moveOne(i)}
                disabled={!!busy || !target}
                data-testid={`move-deal-${i.deal_id}`}
                className="text-xs px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1"
                style={{ color: '#070B5F' }}
              >
                {busy === i.deal_id ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />} Move
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
